import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, CheckCircle2, FileText, BadgeIndianRupee, Clock, Percent, MessageCircle, ArrowRight, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useSite } from "../context/SiteContext";
import { inrCompact, waLink, track } from "../lib/site";
import SectionHead from "../components/site/SectionHead";
import EMICalculator from "../components/site/EMICalculator";
import FAQAccordion from "../components/site/FAQAccordion";
import ProductCard from "../components/site/ProductCard";
import LeadForm from "../components/site/LeadForm";
import NotFound from "./NotFound";

export default function LoanDetail() {
  const { slug } = useParams();
  const { settings, products } = useSite();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`)
      .then((r) => { setProduct(r.data); document.title = r.data.seo_title || `${r.data.name} | BharatBest Fintech`; track("view_loan_product", { product: r.data.name }); })
      .catch(() => setProduct(false))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="container-site py-24 flex justify-center"><Loader2 className="animate-spin text-brand-red" size={32} /></div>;
  if (!product) return <NotFound />;

  const related = products.filter((p) => p.slug !== slug).slice(0, 4);
  const waMsg = (settings.whatsapp_templates?.product || "").replace("{product}", product.name);

  const stats = [
    { icon: BadgeIndianRupee, label: "Loan Amount", value: `${inrCompact(product.min_amount)} – ${inrCompact(product.max_amount)}` },
    { icon: Percent, label: "Interest Rate", value: product.rate },
    { icon: Clock, label: "Tenure", value: product.tenure },
    { icon: FileText, label: "Processing Fee", value: product.processing_fee },
  ];

  return (
    <div data-testid="loan-detail-page">
      <section className="container-site pt-10 lg:pt-14 pb-14">
        <nav className="flex items-center gap-1.5 text-xs text-brand-muted mb-8" aria-label="Breadcrumb" data-testid="breadcrumb">
          <Link to="/" className="hover:text-brand-dark">Home</Link><ChevronRight size={12} />
          <Link to="/loans" className="hover:text-brand-dark">Loans</Link><ChevronRight size={12} />
          <span className="text-brand-dark font-semibold">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow">{product.tagline}</p>
            <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold leading-[1.08] text-brand-dark" data-testid="product-title">{product.name}</h1>
            <p className="mt-5 text-base md:text-lg text-brand-muted leading-relaxed">{product.long_desc}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={`/apply?product=${encodeURIComponent(product.name)}`} data-testid="product-apply-btn" className="btn-primary">Apply Now <ArrowRight size={15} /></Link>
              <a href={waLink(settings, waMsg)} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_click", { product: product.name })} data-testid="product-whatsapp-btn" className="btn-outline !border-[#1FA855]/40 !text-[#1FA855]"><MessageCircle size={15} /> WhatsApp an Expert</a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <img src={product.image} alt={`${product.name} at BharatBest Fintech`} className="rounded-3xl w-full h-[300px] lg:h-[420px] object-cover" data-testid="product-image" />
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-12" data-testid="product-key-facts">
          {stats.map((s, i) => (
            <div key={i} className="card-site p-5">
              <s.icon size={20} className="text-brand-red" />
              <p className="text-xs text-brand-muted font-semibold mt-3">{s.label}</p>
              <p className="font-heading font-bold text-sm text-brand-dark mt-1">{s.value}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-brand-muted mt-4">*Indicative only. Final rates, fees and terms are determined by the respective lending partner based on eligibility and verification.</p>
      </section>

      <section className="bg-white border-y border-black/5 py-16 lg:py-20">
        <div className="container-site grid lg:grid-cols-3 gap-10">
          <div data-testid="product-benefits">
            <h2 className="font-heading text-2xl font-bold text-brand-dark mb-6">Key Benefits</h2>
            <ul className="space-y-3.5">
              {(product.benefits || []).map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-brand-dark/80"><CheckCircle2 size={17} className="text-brand-red mt-0.5 shrink-0" /> {b}</li>
              ))}
            </ul>
          </div>
          <div data-testid="product-eligibility">
            <h2 className="font-heading text-2xl font-bold text-brand-dark mb-6">Eligibility</h2>
            <ul className="space-y-3.5">
              {(product.eligibility || []).map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-brand-dark/80"><CheckCircle2 size={17} className="text-brand-gold mt-0.5 shrink-0" /> {b}</li>
              ))}
            </ul>
          </div>
          <div data-testid="product-documents">
            <h2 className="font-heading text-2xl font-bold text-brand-dark mb-6">Required Documents</h2>
            <ul className="space-y-3.5">
              {(product.documents || []).map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-brand-dark/80"><FileText size={17} className="text-brand-muted mt-0.5 shrink-0" /> {b}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="container-site py-16 lg:py-20">
        <SectionHead eyebrow="Plan Ahead" title={`Estimate Your ${product.name} EMI`} />
        <div className="mt-10"><EMICalculator defaults={{ amount: product.min_amount * 2 }} /></div>
      </section>

      {(product.faqs || []).length > 0 && (
        <section className="container-site pb-16 lg:pb-20">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4"><SectionHead eyebrow="FAQs" title={`${product.name} FAQs`} /></div>
            <div className="lg:col-span-8"><FAQAccordion faqs={product.faqs} testid="product-faq" /></div>
          </div>
        </section>
      )}

      <section className="container-site pb-16 lg:pb-20">
        <LeadForm defaultProduct={product.name} source={`product_${product.slug}`} title={`Enquire About ${product.name}`} compact />
      </section>

      {related.length > 0 && (
        <section className="container-site pb-20 lg:pb-28">
          <SectionHead eyebrow="Explore More" title="Related Loan Products" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
